import React, { useEffect, useState, useCallback } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress,
  FormControlLabel, Grid, InputAdornment, Paper, Slider,
  Stack, Tab, Tabs, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleIcon from '@mui/icons-material/People';
import SpeedIcon from '@mui/icons-material/Speed';
import InfoIcon from '@mui/icons-material/Info';
import PaymentIcon from '@mui/icons-material/Payment';
import SaveIcon from '@mui/icons-material/Save';
import { useParams, useNavigate } from 'react-router-dom';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip as ChartTooltip, ResponsiveContainer,
  CartesianGrid, Legend, BarChart, Bar,
} from 'recharts';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BoltIcon from '@mui/icons-material/Bolt';
import MemoryIcon from '@mui/icons-material/Memory';
import StorageIcon from '@mui/icons-material/Storage';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { adminApi } from '../../api/adminApi';
import PeriodSelector, { initialPeriod } from '../../components/PeriodSelector';
import {
  StatCard, ChartCard, areaGradient, fmtCompact, fmtBytes, fmtGbS, ManualRefreshNotice,
} from '../../components/analytics/AnalyticsKit';
import { TagFilterNotice } from '../dashboard/DashboardPage';

const STATUS_COLOR = { ACTIVE: 'success', SUSPENDED: 'warning', PENDING: 'default', DEACTIVATED: 'error', trial_expired: 'error' };
const PLAN_COLOR   = { trial: 'default', paid: 'primary' };

function InfoRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', py: 1, borderBottom: 1, borderColor: 'divider' }}>
      <Typography variant="body2" color="text.secondary" sx={{ width: 200, flexShrink: 0 }}>{label}</Typography>
      <Typography variant="body2" fontWeight={500}>{value ?? '—'}</Typography>
    </Box>
  );
}

const fmtUsd = (n) => (n === null || n === undefined ? '—' : `$${Number(n).toFixed(2)}`);

// ── Cost Analysis Tab (usage + AWS cost vs revenue) ─────────────────────────────
const DRIVER_LABEL = {
  lambdaMbMs: 'Lambda compute share',
  requests: 'request share',
  dbOps: 'DB-operation share',
  authEvents: 'auth-event share',
  storageBytes: 'storage share',
};

function CostAnalysisTab({ tenantId }) {
  const [period, setPeriod] = useState(() => initialPeriod());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    adminApi.getTenantUsage(tenantId, { start: period.start, end: period.end })
      .then((d) => { if (active) { setData(d); setError(null); } })
      .catch((e) => { if (active) { setError(e.message); } })
      .finally(() => { if (active) { setLoading(false); } });
    return () => { active = false; };
  }, [tenantId, period]);

  const usage = data?.usage || {};
  const series = data?.series || [];
  const cost = data?.cost;
  const revenue = data?.revenue;
  const margin = data?.margin;
  const costUnavailable = !cost || cost.unavailable;
  const shortPeriod = (p) => (p && p.length === 10 ? p.slice(5) : p);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" rowGap={1}>
        <Typography variant="body2" color="text.secondary">
          {data?.range ? `${data.range.start} → ${data.range.end}` : 'Cost analysis'}
          {data?.range?.days ? ` · ${data.range.days} days` : ''}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <ManualRefreshNotice />
          <PeriodSelector value={period} onChange={setPeriod} />
        </Stack>
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          <TagFilterNotice cost={cost} />

          {/* Financial summary — revenue vs cost */}
          <Grid container spacing={2} mb={2.5}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard label="Revenue (billed)" color="#2e7d32" icon={<PaidOutlinedIcon />}
                value={fmtUsd(revenue?.period)} sub={revenue ? `${fmtUsd(revenue.monthly)}/mo` : ''} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard label="AWS Cost (allocated)" color="#c62828" icon={<CloudQueueIcon />}
                value={costUnavailable ? 'N/A' : fmtUsd(cost.total)}
                sub={costUnavailable ? '' : `of ${fmtUsd(cost.platformTotal)} platform spend`} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard label="Margin" value={margin ? fmtUsd(margin.amount) : '—'} icon={<AccountBalanceIcon />}
                color={margin && margin.amount >= 0 ? '#2e7d32' : '#c62828'}
                sub={margin && margin.percent !== null ? `${margin.percent}% of revenue` : ''} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard label="Error Rate" value={`${usage.errorRate ?? 0}%`} icon={<ErrorOutlineIcon />}
                color={(usage.errorRate ?? 0) > 1 ? '#c62828' : '#2e7d32'}
                sub={`${fmtCompact(usage.errors)} errors`} />
            </Grid>
          </Grid>

          {/* Metered consumption — the drivers behind the allocation */}
          <Typography variant="subtitle2" fontWeight={700} mb={1}>Metered Consumption</Typography>
          <Grid container spacing={2} mb={2.5}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard label="API Requests" value={fmtCompact(usage.apiRequests)} icon={<BoltIcon />}
                color="#1565c0" sub={`${fmtCompact(usage.inventoryOps)} inventory ops`} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard label="Lambda Compute" value={fmtGbS(usage.lambdaMbMs)} icon={<MemoryIcon />}
                color="#7b1fa2" sub="duration × memory, metered" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard label="Storage" value={fmtBytes(usage.storageBytes)} icon={<StorageIcon />}
                color="#00897b" sub="S3 + DynamoDB (daily snapshot)" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard label="Data Out" value={fmtBytes(usage.bytesOut)} icon={<SwapVertIcon />}
                color="#ef6c00" sub="API response payloads" />
            </Grid>
          </Grid>

          {/* Usage over time */}
          <Grid container spacing={2} mb={2.5}>
            <Grid item xs={12} md={7}>
              <ChartCard title="Requests & Errors Over Time" subtitle={data?.range ? `${data.range.days}-day window` : ''}>
                {series.length === 0 ? (
                  <Box sx={{ height: 210, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="text.secondary">No usage recorded in this window yet.</Typography>
                  </Box>
                ) : (
                  <ResponsiveContainer width="100%" height={210}>
                    <AreaChart data={series}>
                      <defs>
                        {areaGradient('tReq', '#1565c0')}
                        {areaGradient('tErr', '#c62828')}
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,.08)" />
                      <XAxis dataKey="period" tick={{ fontSize: 10 }} tickFormatter={shortPeriod}
                        tickLine={false} axisLine={false} />
                      <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={40} />
                      <ChartTooltip />
                      <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                      <Area type="monotone" dataKey="apiRequests" name="Requests" stroke="#1565c0"
                        strokeWidth={2} fill="url(#tReq)" dot={false} />
                      <Area type="monotone" dataKey="errors" name="Errors" stroke="#c62828"
                        strokeWidth={2} fill="url(#tErr)" dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </Grid>
            <Grid item xs={12} md={5}>
              <ChartCard title="Lambda Compute Over Time" subtitle="metered GB-seconds per bucket">
                {series.length === 0 ? (
                  <Box sx={{ height: 210, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="text.secondary">No data</Typography>
                  </Box>
                ) : (
                  <ResponsiveContainer width="100%" height={210}>
                    <BarChart data={series.map((s) => ({ ...s, gbs: Math.round((s.lambdaMbMs / 1024 / 1000) * 100) / 100 }))}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,.08)" />
                      <XAxis dataKey="period" tick={{ fontSize: 10 }} tickFormatter={shortPeriod}
                        tickLine={false} axisLine={false} />
                      <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={44} />
                      <ChartTooltip formatter={(v) => `${v} GB-s`} />
                      <Bar dataKey="gbs" name="GB-seconds" fill="#7b1fa2" radius={[5, 5, 0, 0]} maxBarSize={28} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </Grid>
          </Grid>

          {/* AWS cost by service */}
          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
            <Typography variant="subtitle2" fontWeight={700}>AWS Cost by Service</Typography>
            <Tooltip title="Real Cost Explorer costs for tagged inventory-platform resources, allocated to this tenant by its metered share of the driver behind each service: Lambda → compute (duration × memory), API Gateway → requests, DynamoDB → DB ops, Cognito → auth events, S3 → storage bytes. Allocations across tenants always sum to the actual bill.">
              <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>(how is this calculated?)</Typography>
            </Tooltip>
          </Stack>
          {costUnavailable ? (
            <Alert severity="info">
              AWS cost data is unavailable — enable the Cost Explorer API for the account (it can take ~24h to populate) and confirm the admin role has <code>ce:GetCostAndUsage</code>.
            </Alert>
          ) : (
            <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Service</TableCell>
                    <TableCell>Allocation Basis</TableCell>
                    <TableCell align="right">Tenant Share</TableCell>
                    <TableCell align="right">Cost</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(cost.services || []).length === 0 ? (
                    <TableRow><TableCell colSpan={4} align="center" sx={{ py: 2, color: 'text.secondary' }}>No allocated cost yet.</TableCell></TableRow>
                  ) : cost.services.map((s) => (
                    <TableRow key={s.service} hover>
                      <TableCell sx={{ fontSize: 13, fontWeight: 500 }}>{s.service}</TableCell>
                      <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{DRIVER_LABEL[s.driver] || s.driver || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 12, color: 'text.secondary' }}>{s.share}%</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtUsd(s.amount)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                    <TableCell colSpan={2} />
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtUsd(cost.total)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
}

// ── Plan Tab ──────────────────────────────────────────────────────────────────
function PlanTab({ tenant, onSaved }) {
  const [plan, setPlan] = useState(tenant.plan || 'trial');
  const [discountPercent, setDiscountPercent] = useState(tenant.discountPercent || 0);
  const [discountAppliesToAddons, setDiscountAppliesToAddons] = useState(tenant.discountAppliesToAddons || false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const hasChanges = plan !== (tenant.plan || 'trial')
    || discountPercent !== (tenant.discountPercent || 0)
    || discountAppliesToAddons !== (tenant.discountAppliesToAddons || false);

  const switchingToPaid = plan === 'paid' && tenant.plan === 'trial';
  const isLocked = tenant.status === 'trial_expired';

  const handleSave = async () => {
    setSaving(true);
    setToast(null);
    try {
      const result = await adminApi.updateTenantPlan(tenant.tenantId, {
        plan,
        discountPercent: discountPercent || 0,
        discountAppliesToAddons,
      });
      setToast({ severity: 'success', message: `Plan updated to ${plan}.` });
      if (onSaved) { onSaved(result.tenant); }
    } catch (e) {
      setToast({ severity: 'error', message: e.message || 'Failed to update plan.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} mb={2}>Subscription Plan</Typography>

          <Typography variant="body2" color="text.secondary" mb={1}>
            Toggle this tenant between trial and paid. Switching to paid auto-clears trial expiry dates and unlocks the tenant if locked.
          </Typography>

          <ToggleButtonGroup
            value={plan}
            exclusive
            onChange={(_, v) => { if (v) { setPlan(v); } }}
            fullWidth
            sx={{ mb: 2 }}
          >
            <ToggleButton value="trial" color="warning">Trial</ToggleButton>
            <ToggleButton value="paid" color="primary">Paid</ToggleButton>
          </ToggleButtonGroup>

          {switchingToPaid && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              Switching to paid will set status to ACTIVE{isLocked ? ', remove locked state,' : ''} and clear trial expiry dates.
            </Alert>
          )}

          {plan === 'trial' && tenant.trialEndsAt && (
            <Typography variant="caption" color="text.secondary" display="block">
              Trial expires: {new Date(tenant.trialEndsAt).toLocaleDateString()}
            </Typography>
          )}
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} mb={2}>Pricing Discount</Typography>

          <Typography variant="body2" color="text.secondary" mb={2}>
            Set a percentage discount on the base plan price for this tenant. Optionally apply it to add-on costs as well.
          </Typography>

          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" fontWeight={600} color="text.secondary" gutterBottom>
                Discount: {discountPercent}%
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Slider
                  value={discountPercent}
                  onChange={(_, v) => setDiscountPercent(v)}
                  min={0}
                  max={100}
                  step={1}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => `${v}%`}
                  sx={{ flex: 1 }}
                />
                <TextField
                  value={discountPercent}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    if (!isNaN(v) && v >= 0 && v <= 100) { setDiscountPercent(v); }
                  }}
                  size="small"
                  sx={{ width: 90 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                />
              </Stack>
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={discountAppliesToAddons}
                  onChange={(e) => setDiscountAppliesToAddons(e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Apply discount to add-on costs</Typography>
                  <Typography variant="caption" color="text.secondary">
                    When enabled, the {discountPercent}% discount also applies to all feature add-on prices
                  </Typography>
                </Box>
              }
            />
          </Stack>
        </Paper>
      </Grid>

      {/* Summary & Save */}
      <Grid item xs={12}>
        {toast && (
          <Alert severity={toast.severity} sx={{ mb: 2, borderRadius: 2 }} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        )}
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            disabled={!hasChanges || saving}
            onClick={handleSave}
          >
            Save Plan Changes
          </Button>
        </Stack>
      </Grid>
    </Grid>
  );
}

export default function TenantDetailPage() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(0);

  const load = useCallback(() => {
    setLoading(true);
    adminApi.getTenant(tenantId)
      .then((t) => { setTenant(t.tenant || t); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const handlePlanSaved = (updatedTenant) => {
    setTenant(updatedTenant);
  };

  if (loading) { return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>; }
  if (error) { return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>; }
  if (!tenant) { return null; }

  return (
    <Box sx={{ p: 3, maxWidth: 1100 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>{tenant.name}</Typography>
          <Stack direction="row" spacing={1} mt={0.5}>
            <Chip label={tenant.plan?.toUpperCase()} size="small" color={PLAN_COLOR[tenant.plan] || 'default'} />
            <Chip label={tenant.status} size="small" color={STATUS_COLOR[tenant.status] || 'default'} variant="outlined" />
            {tenant.discountPercent > 0 && (
              <Chip label={`${tenant.discountPercent}% discount`} size="small" color="success" variant="outlined" />
            )}
            <Typography variant="caption" color="text.secondary" alignSelf="center">
              {tenant.tenantId}
            </Typography>
          </Stack>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<SettingsIcon />}
            onClick={() => navigate(`/tenants/${tenantId}/features`)}>
            Manage Features
          </Button>
          <Button variant="outlined" startIcon={<PeopleIcon />}
            onClick={() => navigate(`/tenants/${tenantId}/users`)}>
            Manage Users
          </Button>
        </Stack>
      </Stack>

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab icon={<InfoIcon fontSize="small" />} iconPosition="start" label="Overview" />
        <Tab icon={<PaymentIcon fontSize="small" />} iconPosition="start" label="Plan" />
        <Tab icon={<SpeedIcon fontSize="small" />} iconPosition="start" label="Usage & Cost" />
      </Tabs>

      {/* Overview tab */}
      {tab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} mb={1}>Tenant Details</Typography>
              <InfoRow label="Tenant ID"     value={tenant.tenantId} />
              <InfoRow label="Name"          value={tenant.name} />
              <InfoRow label="Slug"          value={tenant.slug} />
              <InfoRow label="Plan"          value={tenant.plan} />
              <InfoRow label="Status"        value={tenant.status} />
              <InfoRow label="Admin Email"   value={tenant.adminEmail} />
              <InfoRow label="Created"       value={new Date(tenant.createdAt).toLocaleString()} />
              <InfoRow label="Last Updated"  value={new Date(tenant.updatedAt).toLocaleString()} />
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} mb={1}>Plan Limits</Typography>
              <InfoRow label="Max Users"          value={tenant.settings?.maxUsers} />
              <InfoRow label="Max API Keys"       value={tenant.settings?.maxApiKeys} />
              <InfoRow label="Max Items"          value={(tenant.settings?.maxItems ?? tenant.settings?.maxProducts)?.toLocaleString()} />
              <InfoRow label="Max Warehouses"     value={tenant.settings?.maxWarehouses} />
              <InfoRow label="API Rate Limit/min" value={tenant.settings?.apiRateLimitPerMinute?.toLocaleString()} />
              <InfoRow label="Session Timeout"    value={`${tenant.settings?.sessionTimeoutMinutes ?? 480} min`} />
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Plan tab */}
      {tab === 1 && (
        <PlanTab tenant={tenant} onSaved={handlePlanSaved} />
      )}

      {/* Usage & Cost Analysis tab */}
      {tab === 2 && <CostAnalysisTab tenantId={tenantId} />}
    </Box>
  );
}
